'use client';

/**
 * Single component that emits both client-render telemetry signals against
 * /redirect/beacon:
 *
 *   - `html_rendered` — fired by an SSR'd 1x1 <img>. The browser fetches it
 *     as soon as it parses the HTML, so any client that loads embedded
 *     resources increments this counter, even if JS is disabled.
 *
 *   - `js_painted` — fired client-side from useEffect via PerformanceObserver
 *     on the `paint` entry type. Only increments when JS executed and the
 *     browser reached first-contentful-paint, so naive scrapers and bots
 *     that don't run JS skip it.
 *
 * Together they bracket the gap between server-side `turnstile.request`
 * (every gated render) and `turnstile.response` (every verify POST):
 *
 *   request → html_rendered → js_painted → response
 *
 * Drop into any handler that wants this funnel; the component lives at the
 * top of shared-components so the html_rendered/js_painted signals aren't
 * coupled to Turnstile gating specifically.
 *
 * Marked 'use client' because the js_painted effect needs PerformanceObserver
 * and useEffect. Next.js still pre-renders client components on the server
 * for the initial HTML response, so the <img> ships in the first byte and
 * the html_rendered beacon fires before hydration just like a server-only
 * component would.
 */
import React, { useEffect } from 'react';

interface Props {
  productType: string;
}

type BeaconKind = 'html_rendered' | 'js_painted';

function beaconUrl(
  kind: BeaconKind,
  productType: string,
  cacheBust: boolean
): string {
  const pt = encodeURIComponent(productType || 'unknown');
  // The cache-buster is only safe to apply client-side: a different value
  // between server-render and hydration would trip a React hydration
  // mismatch on the <img>. The /redirect/beacon response sets
  // Cache-Control: no-store on every reply, so omitting `t=` from the SSR
  // pixel is safe; we still include it on the client-fired beacon as
  // belt-and-braces against any intermediate proxy that ignores no-store.
  const t = cacheBust ? `&t=${Date.now()}` : '';
  return `/redirect/beacon?kind=${kind}&pt=${pt}${t}`;
}

export default function Beacon({ productType }: Props): JSX.Element {
  useEffect(() => {
    const url = beaconUrl('js_painted', productType, true);
    // new Image() is fire-and-forget, no CORS preflight, and survives the
    // page navigating away — same GET shape as the SSR pixel so both
    // beacons hit the endpoint with identical caching behaviour.
    const fire = () => {
      new Image().src = url;
    };

    // If FCP already happened before this effect ran (component mounted
    // late after hydration, or a long task delayed the effect), fire now
    // rather than wait for an observer event that will never come.
    if (typeof PerformanceObserver === 'undefined') {
      fire();
      return;
    }
    const existing = performance.getEntriesByName('first-contentful-paint');
    if (existing.length) {
      fire();
      return;
    }

    let fired = false;
    const observer = new PerformanceObserver((list) => {
      if (fired) return;
      if (list.getEntriesByName('first-contentful-paint').length) {
        fired = true;
        fire();
        observer.disconnect();
      }
    });
    try {
      observer.observe({ type: 'paint', buffered: true });
    } catch {
      // Older browsers that don't accept `type`/`buffered` for the paint
      // entry. Fall back to firing immediately rather than losing the signal.
      fire();
    }
    return () => observer.disconnect();
  }, [productType]);

  return (
    <img
      src={beaconUrl('html_rendered', productType, false)}
      alt=""
      width={1}
      height={1}
      // No-referrer keeps query strings (utm_*, etc.) from the original page
      // out of the beacon's request log.
      referrerPolicy="no-referrer"
      aria-hidden="true"
      // Inline style so the pixel doesn't depend on a stylesheet load order
      // and doesn't introduce an extra class to the global SCSS surface.
      style={{
        position: 'absolute',
        width: 1,
        height: 1,
        opacity: 0,
        pointerEvents: 'none',
      }}
    />
  );
}
